import { CommonModule, NgFor, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import swal from 'sweetalert2';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Incidencias } from 'src/app/shared/model/incidencias/incidencia.model';
import { Proyectos } from 'src/app/shared/model/incidencias/proyectos.model';
import { IncidenciasService } from '../incidencias.service';

@Component({
  selector: 'app-incidenciacliente',
  standalone: true,
  templateUrl: './incidenciacliente.component.html',
  styleUrls: ['./incidenciacliente.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    NgFor,
    NgIf,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
  ],
})
export class IncidenciaCliente implements OnInit {
  filtro = new ConsultaList<Incidencias>();
  incidencias: Incidencias[] = [];
  proyectos: Proyectos[] = [];

  textoBusqueda = '';
  selectProyecto: number = null;
  selectEstatus: string = null;
  selectPrioridad: string = null;
  fechaIni: string = null;
  fechaFin: string = null;

  totalRegistros = 0;
  totalPages = 0;
  currentPage = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50];
  readonly estatusOptions = ['ABIERTO', 'OBSERVACIONES', 'PROCESO', 'RESUELTO', 'REVISION', 'VOBO', 'CANCELADO'];
  readonly prioridadOptions = ['ALTA', 'MEDIA', 'BAJA'];
  loading = false;
  importingId: number = null;

  constructor(private incidenciasService: IncidenciasService) {}

  ngOnInit(): void {
    this.filtro.bean = new Incidencias();
    this.consultaProyectos();
    this.consultaPagina(1);
  }

  consultaProyectos(): void {
    this.incidenciasService.consultaproyectos(new Proyectos()).subscribe((res) => {
      this.proyectos = res?.list ?? [];
    });
  }

  buscar(): void {
    this.consultaPagina(1);
  }

  limpiarFiltros(): void {
    this.textoBusqueda = '';
    this.selectProyecto = null;
    this.selectEstatus = null;
    this.selectPrioridad = null;
    this.fechaIni = null;
    this.fechaFin = null;
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.loading = true;
    const bean = new Incidencias();
    bean.titulo = this.textoBusqueda?.trim() || null;
    bean.id_proyecto = this.selectProyecto || null;
    bean.estatus = this.selectEstatus || null;
    bean.prioridad = this.selectPrioridad || null;
    bean.fec_ini = this.fechaIni || null;
    bean.fec_fin = this.fechaFin || null;

    this.filtro.bean = bean;
    this.filtro.regInicial = (page - 1) * this.pageSize;
    this.filtro.regXPagina = this.pageSize;

    this.incidenciasService.consultarBandejaIncidenciasCliente(this.filtro).subscribe({
      next: (res) => {
        this.incidencias = res?.list ?? [];
        this.totalRegistros = Number(res?.pTotalItems ?? this.incidencias.length);
        this.totalPages = this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
        this.loading = false;
      },
      error: () => {
        this.incidencias = [];
        this.totalRegistros = 0;
        this.totalPages = 0;
        this.loading = false;
      },
    });
  }

  importar(incidencia: Incidencias): void {
    if (incidencia.importada || this.importingId) {
      return;
    }

    swal
      .fire({
        title: 'Importar incidencia a QA',
        text: `Se creará una incidencia QA vinculada al folio cliente #${incidencia.id_incidencia_proyecto || incidencia.id}.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Importar',
        cancelButtonText: 'Cancelar',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-secondary',
        },
      })
      .then((result) => {
        if (!result.isConfirmed) {
          return;
        }

        this.importingId = incidencia.id;
        this.incidenciasService.importarIncidenciaCliente(incidencia.id).subscribe({
          next: () => {
            this.importingId = null;
            swal.fire({
              text: 'La incidencia se importó al flujo de QA.',
              icon: 'success',
              buttonsStyling: false,
              customClass: { confirmButton: 'btn btn-success' },
            });
            this.consultaPagina(this.currentPage);
          },
          error: () => {
            this.importingId = null;
            swal.fire({
              text: 'No fue posible importar la incidencia.',
              icon: 'error',
              buttonsStyling: false,
              customClass: { confirmButton: 'btn btn-danger' },
            });
          },
        });
      });
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }
    const pages = new Set<number>([1, this.totalPages, this.currentPage - 1, this.currentPage, this.currentPage + 1]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);
    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }
}
